import LiveReport from './LiveReport';
import ReportChrome from './ReportChrome';
import {
    AVATAR_COLORS,
    GUIDANCE_EXTRA,
    GUIDANCE_ROWS,
    PLACEMENT_FOOTNOTES,
    PLACEMENT_ROWS,
    PLACEMENTS_EXTRA,
    STORIES_EXTRA,
    STORIES_FOOTNOTES,
    STORIES_ROWS,
    UPLOAD_ROWS,
} from './mockReportData';
import { ReportDef, initials } from './reportRegistry';

const AlumniCell = ({ name, batch, index }: { name: string; batch: string; index: number }) => (
    <div className="cr-alum">
        <div className="cr-avatar" style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>
            {initials(name)}
        </div>
        <div>
            <div className="cr-alum-name">{name}</div>
            <div className="cr-alum-sub">{batch}</div>
        </div>
    </div>
);

const Check = () => (
    <td>
        <input type="checkbox" className="form-check-input" readOnly />
    </td>
);

export const EventsReport = ({ report }: { report: ReportDef }) => <LiveReport report={report} kind="events" />;

const AHLI_CLASS: Record<string, string> = {
    Powerhouse: 'cr-tag-green',
    'Sleeping Giant': 'cr-tag-navy',
    Overachiever: 'cr-tag-gold',
    'Question Mark': 'cr-tag-orange',
};
const SPM_CLASS: Record<string, string> = {
    'Core Powerhouse': 'cr-tag-green',
    'Elite but Limited': 'cr-tag-navy',
    'Safety Net': 'cr-tag-gold',
    'Dead Zone': 'cr-tag-red',
};

export const PlacementsReport = ({ report }: { report: ReportDef }) => (
    <ReportChrome
        report={report}
        extraFilters={PLACEMENTS_EXTRA}
        countText="Showing 1–8 of 2,146 placement records"
        sortOptions={['CTC (high to low)', 'Batch Year', 'Alumni Name']}
        columns={['Alumni', 'Program / Batch', 'Placed Company', 'Current Company', 'Designation', 'CTC', 'AHLI', 'SPM', 'Percentile']}
        footnotes={PLACEMENT_FOOTNOTES}
        moreFiltersLabel="More Filters (5)">
        {PLACEMENT_ROWS.map((r, i) => (
            <tr key={r.name}>
                <Check />
                <td>
                    <AlumniCell name={r.name} batch={r.batch} index={i} />
                </td>
                <td>{r.batch}</td>
                <td>
                    <div>
                        {r.placed}
                        {r.lapsed && <span className="cr-lapsed">LAPSED</span>}
                    </div>
                    {!r.switched && <div className="cr-prev">Still here</div>}
                </td>
                <td>
                    {r.current}
                    {r.switched && <div className="cr-prev">← from {r.placed}</div>}
                </td>
                <td>{r.desig}</td>
                <td className="fw-semibold">{r.ctc}</td>
                <td>
                    {r.ahli ? (
                        <span className={`cr-tag ${AHLI_CLASS[r.ahli] || ''}`}>{r.ahli}</span>
                    ) : (
                        <span className="cr-blank">—</span>
                    )}
                </td>
                <td>
                    {r.spm ? (
                        <span className={`cr-tag ${SPM_CLASS[r.spm] || ''}`}>{r.spm}</span>
                    ) : (
                        <span className="cr-blank">—</span>
                    )}
                </td>
                <td>
                    <span className="cr-tag cr-tag-gold">{r.pct}</span>
                </td>
            </tr>
        ))}
    </ReportChrome>
);

export const RewardsReport = ({ report }: { report: ReportDef }) => <LiveReport report={report} kind="rewards" />;

export const GuidanceReport = ({ report }: { report: ReportDef }) => {
    const typeClass: Record<string, string> = {
        Mentorship: 'cr-tag-green',
        Webinar: 'cr-tag-navy',
        'Guest Lecture': 'cr-tag-gold',
        'Short-term Project': 'cr-tag-purple',
    };
    return (
        <ReportChrome
            report={report}
            extraFilters={GUIDANCE_EXTRA}
            countText="Showing 1–8 of 418 alumni"
            sortOptions={['Sessions delivered', 'Last activity', 'Alumni Name']}
            columns={['Alumni', 'Program / Batch', 'Activity Type', 'Topic / Session', 'Initiative', 'Sessions', 'Last Activity']}
            moreFiltersLabel="More Filters (4)">
            {GUIDANCE_ROWS.map((r, i) => (
                <tr key={r[0] + r[3]}>
                    <Check />
                    <td>
                        <AlumniCell name={r[0]} batch={r[1]} index={i} />
                    </td>
                    <td>{r[1]}</td>
                    <td>
                        <span className={`cr-tag ${typeClass[r[2]] || ''}`}>{r[2]}</span>
                    </td>
                    <td>{r[3]}</td>
                    <td>{r[4]}</td>
                    <td className="fw-semibold">{r[5]}</td>
                    <td>{r[6]}</td>
                </tr>
            ))}
        </ReportChrome>
    );
};

export const StoriesReport = ({ report }: { report: ReportDef }) => {
    const segClass: Record<string, string> = {
        'Super Achiever': 'cr-tag-gold',
        Achiever: 'cr-tag-navy',
        Hustler: 'cr-tag-orange',
        'Late Bloomer': 'cr-tag-purple',
    };
    const statusClass: Record<string, string> = {
        'Published on Website': 'cr-tag-green',
        'Published + Social': 'cr-tag-green',
        Draft: 'cr-tag-grey',
        'Ready for Review': 'cr-tag-gold',
        'Not Published': 'cr-tag-red',
    };
    return (
        <ReportChrome
            report={report}
            extraFilters={STORIES_EXTRA}
            countText="Showing 1–8 of 196 alumni"
            sortOptions={['Marketability (high to low)', 'Segment', 'Batch Year']}
            columns={['Alumni', 'Program / Batch', 'Segment', 'Current Role', 'Story Headline', 'Story Status']}
            footnotes={STORIES_FOOTNOTES}
            moreFiltersLabel="More Filters (4)">
            {STORIES_ROWS.map((r, i) => (
                <tr key={r[0]}>
                    <Check />
                    <td>
                        <AlumniCell name={r[0]} batch={r[1]} index={i} />
                    </td>
                    <td>{r[1]}</td>
                    <td>
                        <span className={`cr-tag ${segClass[r[2]] || ''}`}>{r[2]}</span>
                    </td>
                    <td>{r[3]}</td>
                    <td className="fst-italic">{r[4]}</td>
                    <td>
                        <span className={`cr-tag ${statusClass[r[5]] || ''}`}>{r[5]}</span>
                    </td>
                </tr>
            ))}
        </ReportChrome>
    );
};

export const UploadLogReport = ({ report }: { report: ReportDef }) => (
    <ReportChrome
        report={report}
        extraFilters={[
            { label: 'Report', active: true, summary: 'Placements' },
            { label: 'Uploaded by' },
            { label: 'Status' },
            { label: 'Date Range' },
        ]}
        hideStandardFilters
        actions="upload"
        countText=""
        sortOptions={[]}
        columns={['Timestamp', 'Report', 'File Name', 'Uploaded By', 'Rows Added', 'Rows Updated', 'Skipped', 'Status', 'Actions']}>
        {UPLOAD_ROWS.map((r) => (
            <tr key={r[0] + r[2]}>
                <td>{r[0]}</td>
                <td>
                    <span className="cr-tag cr-tag-navy">{r[1]}</span>
                </td>
                <td>{r[2]}</td>
                <td>{r[3]}</td>
                <td className="fw-semibold">{r[4]}</td>
                <td className="fw-semibold">{r[5]}</td>
                <td>{r[6]}</td>
                <td>
                    <span className={`cr-status-dot ${r[8]}`}></span>
                    {r[7]}
                </td>
                <td>
                    <button type="button" className="cr-link">
                        View
                    </button>
                </td>
            </tr>
        ))}
    </ReportChrome>
);
